import { Coverage } from '@medplum/fhirtypes';
import { COVERAGE_TYPE_CODESYSTEM, INSURANCE_COVERAGE_TYPE_CODE } from './constants';

/**
 * The patient's insurance card as FHIR: a `Coverage` (type `insurance`), its payor insurer
 * `Organization`, and a `RelatedPerson` policyholder when the plan isn't the patient's.
 * bariendo-be writes it at signup (`coverage.service.ts`, `coverage.constants.ts`, BAR-2905)
 * and the `update-insurance-coverage` bot edits it. Mirrors bariendo-be — keep in sync. The
 * prebuilt Codings (`INSURER_ORGANIZATION_TYPE`, `GROUP_CLASS_CODING`, `POLICYHOLDER_ROLES`) are in
 * `codings.ts`.
 */

/** `Organization.type` of a payer on a state's insurer list: HL7 organization-type `ins` (BAR-2894). */
export const ORGANIZATION_TYPE_CODESYSTEM = 'http://terminology.hl7.org/CodeSystem/organization-type';
export const INSURER_ORGANIZATION_TYPE_CODE = 'ins';

/** `Coverage.class[].type` for the group number. */
export const COVERAGE_CLASS_CODESYSTEM = 'http://terminology.hl7.org/CodeSystem/coverage-class';
export const GROUP_CLASS_CODE = 'group';

/**
 * `Coverage.type`'s second coding on an insurance Coverage: how the patient gets the plan
 * (bariendo-be `COVERAGE_SOURCE_CODESYSTEM`). The codes are the signup `coverage_source` options
 * in bariendo-be's `eligibility-steps.config.ts`. A Coverage bariendo-be opens for an insurance
 * episode with no signup card has no source coding.
 */
export const COVERAGE_SOURCE_CODESYSTEM = 'http://bariendo.com/fhir/CodeSystem/coverage-source';
export const COVERAGE_SOURCES = ['job', 'self', 'tricare', 'va', 'medicare', 'medicaid', 'other'] as const;
export type CoverageSource = (typeof COVERAGE_SOURCES)[number];

/** `Coverage.relationship`: the beneficiary's relationship to the subscriber. */
export const SUBSCRIBER_RELATIONSHIP_CODESYSTEM = 'http://terminology.hl7.org/CodeSystem/subscriber-relationship';

export const SUBSCRIBER_RELATIONSHIPS = ['self', 'spouse', 'child', 'other'] as const;
export type SubscriberRelationship = (typeof SUBSCRIBER_RELATIONSHIPS)[number];

export function isInsuranceCoverage(coverage: Coverage): boolean {
  return (
    coverage.type?.coding?.some(
      (c) => c.system === COVERAGE_TYPE_CODESYSTEM && c.code === INSURANCE_COVERAGE_TYPE_CODE
    ) ?? false
  );
}

/** `Organization.identifier` system carrying bariendo-be's permanent payer key, e.g. `cigna` (BAR-2894). */
export const INSURANCE_PAYER_KEY_IDENTIFIER_SYSTEM = 'http://bariendo.com/fhir/identifier/insurance-payer-key';

/** `Organization.meta.tag` system naming each state whose list an insurer is on, e.g. `SC`. */
export const INSURANCE_PAYER_STATE_TAG_SYSTEM = 'http://bariendo.com/fhir/CodeSystem/insurance-payer-state';
